
import { AfterContentInit, AfterViewInit, Component, ElementRef, EventEmitter,
         HostListener, Input, OnChanges, Output, SimpleChanges, ViewChild
       } from '@angular/core';
import { SVG, Svg, G, Rect, Text as SvgText, Element as SvgElement
       } from '@svgdotjs/svg.js';
import { StageConstants, ToolboxConstants } from 'app/core/constants';
import { Utils } from 'core/utils';
import { StageUtils } from 'core/utils.stage';
import { TextPlaceholder
       } from '../../collage-designer/stage/placeholder/text-placeholder';
import { scaleModel, Text, TextChunk } from '../../text/text.model';

interface CopiedDocumentElements
{
  document: Svg;
  content: G;
  text: SvgText;
}

@Component({
    selector: 'placeholder-textarea',
    templateUrl: './placeholder-textarea.component.html',
    styleUrls: ['./placeholder-textarea.component.scss'],
    standalone: false
})
export class PlaceholderTextareaComponent
    implements AfterViewInit, AfterContentInit, OnChanges
{

  private static readonly UPDATE_DELAY_PLACEHOLDER_AREA: number = 1000; /*ms*/

  @Input('textModel') public textModel: Text | undefined = undefined;
  @Input('placeholder') public placeholder: TextPlaceholder;
  @Output('textChange') public textChange: EventEmitter<Array<TextChunk>> =
    new EventEmitter<Array<TextChunk>>();

  public displayModel: Text | undefined = undefined;
  public textOffsetLeft: string = '0px';
  public textOffsetTop: string = '0px';

  @ViewChild('background', {static: true}) private background: ElementRef;
  private backgroundTextWrapper: Svg | undefined;
  private backgroundScaleFactor: number = 1;

  constructor(private hostElement: ElementRef)
  {}

  public ngOnChanges(changes: SimpleChanges): void
  {
    if ('placeholder' in changes) {
      this.catchBackgroundDocument();
      this.updateBackground();
    }

    if ('textModel' in changes) {
      this.updateTextModel();
    }
  }

  public ngAfterViewInit(): void {
    setTimeout(() => {
      this.updateTextPosition();
      this.updateBackgroundScaleFactor();
      this.updateTextModel();
    }, PlaceholderTextareaComponent.UPDATE_DELAY_PLACEHOLDER_AREA);
  }

  public onTextChanged(chunks: Array<TextChunk>): void
  {
    this.textChange.emit(chunks);
  }

  private catchBackgroundDocument(): void
  {
    if (! this.placeholder) {
      return;
    }

    const copied = this.clonePlaceholderDocument(
        this.placeholder,
        SVG(this.background.nativeElement) as Svg);

    this.backgroundTextWrapper = (copied.text as SvgElement).parent() as Svg;
    const rotation = this.getRotation(this.backgroundTextWrapper);
    this.addShadowOverlay(
      this.backgroundTextWrapper, copied.content, copied.document, rotation);
    copied.content.rotate(-rotation);
    this.setBackgroundViewbox(this.backgroundTextWrapper, copied.document);
    setTimeout(() => {
      copied.text.hide();
    }, ToolboxConstants.AFTER_TEXT_TOOLBOX_ANIMATION);
  }

  private getRotation(textWrapper: Svg): number
  {
    return (<any>textWrapper).parent().transform().rotate;
  }

  private addShadowOverlay(
    textWrapper: Svg, content: G, backgroundDocument: Svg,
    rotation: number): void
  {
    const { x, y, width, height } = backgroundDocument.bbox();
    const diagonal  = Math.sqrt(Math.pow(width, 2) + Math.pow(height, 2));
    const docBox = {
      x: x - diagonal / 2,
      y: y - diagonal / 2,
      width: diagonal * 2,
      height: diagonal * 2,
    };

    const wrapperRbox = (<any>textWrapper).rbox(backgroundDocument);
    const wrapperBox = {
      x: wrapperRbox.x + wrapperRbox.width / 2
          - (textWrapper.width() as number)/ 2,
      y: wrapperRbox.y + wrapperRbox.height / 2
          - (textWrapper.height() as number)/ 2,
      width: textWrapper.width() as number,
      height: textWrapper.height() as number
    };

    const pathData = (
      `M ${docBox.x} ${docBox.y} ` +
      `h ${docBox.width} ` +
      `v ${docBox.height} ` +
      `h -${docBox.width} ` +
      `v -${docBox.height} ` +
      `M ${wrapperBox.x} ${wrapperBox.y} ` +
      `l 0 ${wrapperBox.height} ` +
      `l ${wrapperBox.width} 0 ` +
      `l 0 ${-wrapperBox.height} ` +
      `l ${-wrapperBox.width} 0 ` +
      `z`
    );

    content.path(pathData).fill('rgba(255, 255, 255, 0.6)').rotate(
        rotation,
        (wrapperBox.x + wrapperBox.width / 2),
        (wrapperBox.y + wrapperBox.height / 2));
  }

  private setBackgroundViewbox(
    textWrapper: Svg, backgroundDocument: Svg): void
  {
    const PADDING_PERCENT = 10;

    const box = (<any>textWrapper).rbox(backgroundDocument);
    const padding = box.width * (PADDING_PERCENT / 100);

    box.x -= padding / 2;
    box.y -= padding / 2;
    box.width += padding;
    box.height += padding;

    backgroundDocument.viewbox(box);
  }

  private clonePlaceholderDocument(
    placeholder: TextPlaceholder, target: Svg): CopiedDocumentElements
  {
    const originalText = placeholder.element as SvgElement;
    const originalDocument = (<any>originalText).root();

    originalText.data('original-id', originalText.id());

    const contentGroup = this.copyDocument(originalDocument, target);

    const backgroundText = (<any>target)
      .find(`[data-original-id=\"${originalText.id()}\"]`)
      .at(0) as SvgText;

    originalText.data('original-id', null); // eslint-disable-line

    return {
      document: target,
      content: contentGroup,
      text: backgroundText,
    };
  }

  private copyDocument(source: Svg, target: Svg): G
  {
    const children = source.children() as Array<SvgElement>;
    target.clear();
    const content = target.group();

    for (const child of children) {
      const cloned = child.clone();
      cloned.addTo(content);
    }

    const elementsToRemove = StageUtils.elementsBySelectors(
        content.node,
        StageConstants.DESIGNER_ONLY_CLASSES.map(c => '.' + c));
    for (const element of elementsToRemove) {
      element.remove();
    }

    return content;
  }

  public ngAfterContentInit(): void {
    if (Utils.isLargeDevice()) {
      return;
    }

    this.updateTextPosition();
  }

  @HostListener('window:resize', [])
  private updateBackground(): void
  {
    this.updateTextPosition();
    this.updateBackgroundScaleFactor();
    this.updateTextModel();
  }

  private updateTextPosition(): void
  {
    if (! this.backgroundTextWrapper) {
      return;
    }

    const origin = this.hostElement.nativeElement.getBoundingClientRect();
    const box = this.backgroundTextWrapper.node.getBoundingClientRect();

    this.textOffsetLeft = `${box.left - origin.left}px`;
    this.textOffsetTop = `${box.top - origin.top}px`;
  }

  private updateBackgroundScaleFactor(): void
  {
    if (! this.backgroundTextWrapper) {
      return;
    }

    // This calculation warrants some explanation.
    //
    // - Let f, m and g be functions that transform coordinate systems:
    //   - f translates template coordinates to points.
    //   - m translates template coordinates to screen coordinates.
    //   - g translates points to screen coordinates.
    //   — we are looking for g.
    //
    // - Let T be the "template native" coordinate system.
    //     (i.e. the one used by the background SVG.)
    //
    // - Let P be the "typographic point" coordinate system.
    //
    // - Let S be the "screen" coordinate system.
    //
    // - the signatures of f,g and m are:
    //   - f: T → P
    //   - m: T → S
    //   - g: P → S
    //
    // - To find g we can express it as a composition of m and f:
    //   - g¯¹ = f . m¯¹; (S → P) = (T -> P) . (S → T)
    //   ⇒ g = (f . m¯¹)¯¹ = f¯¹ . m
    //
    // - Identify the functions:
    //   - f is placeholder.toFontSizePt. Its inverse is
    //       placeholder.toTemplateFontSize
    //   - m is the ratio of the placeholder height "on screen" to its height
    //       in its own coordinate system.
    //   - g is the backgroundScaleFactor

    const bounds = this.backgroundTextWrapper
      .find('rect')
      .at(0) as Rect;

    const boundsRboxHeight = bounds.rbox().height;
    const bgTextWrapperHeight = this.backgroundTextWrapper.height() as number;

    this.backgroundScaleFactor =
      this.placeholder.toTemplateFontSize(
        boundsRboxHeight / bgTextWrapperHeight);
  }

  private updateTextModel(): void
  {
    if (! this.textModel) {
      return;
    }

    this.displayModel = scaleModel(this.textModel, this.backgroundScaleFactor);
  }
}
